import { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import { find, reject } from 'lodash-es'
import { Vector2 } from 'three'
import WallEdit from '@editor/components/WallEdit'
import {
  useHideFloorplanElements,
  useFloorplanElementTypeToSelectable,
  useCanvasEvent,
  useFloorplanClickListener,
} from '@editor/hooks'
import { useCameraStore } from '@modugen/ui-toolkit'
import { coordinateEventName } from '@ui/events'
import { closeSnackbar, enqueueSnackbar } from '@ui/snackbar'
import {
  useModelStore,
  useEditElementStore,
  useControlStore,
  roofStoreyKey,
  useTapelineStore,
} from '@editorStores'
import {
  useElementTypes,
  useSelectionMode,
  useTypeInteraction,
  useStoreyImages,
} from '@editorHooks'
import useVisibleModel from 'src/components/pages/Editor/hooks/useVisibleModel'
import {
  useHotkeys_0to9WithNumpadAndLongPress,
  useHotkeys_Ctrl_0to9,
  useHotkeys_ShortAndLongPress,
} from 'src/hooks/useHotkeysShiftX'
import { useFilterState } from '../../hooks/useFilterState'
import useStructuralPlanningSelection from '../../hooks/useStructuralPlanningSelection'
import {
  VerticalSlabDrawer2D,
  BeamDrawer,
  ColumnDrawer,
  WallDrawer,
  VerticalRoofDrawer2D,
  PurlinDrawer,
} from './components'
import BeamEdit from './components/BeamEdit'
import PurlinEdit from './components/PurlinEdit'

interface Props {
  selectedElement?: string
  mode?: StructuralPlanningModes
}

const FloorplanDrawer = ({ mode }: Props): ReactElement => {
  const {
    params: { storey },
    modes: {
      isDrawMode,

      isWallRipMode,
      isDrawingPurlinsMode,
    },
    actions: { setStorey },
  } = useStructuralPlanningSelection()

  const visibleModel = useVisibleModel()

  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const activeElement = useEditElementStore(state => state.activeElement)

  const { toggleStoreyImageVisibilityIn2D } = useStoreyImages()

  const selectionModeKey = useControlStore(state => state.selectionModeKey)
  const { isSelectionMode } = useSelectionMode()
  // Check if coordinate input mode is active
  const isCoordinateInputMode = selectionModeKey?.startsWith(coordinateEventName)

  const { showVerticalSlabs, showRoofSlabs, showPurlins, showVerticalRoofSlabs, showSlabs } =
    useFilterState()

  const availableStoreys = useModelStore(state => state.availableStoreys)
  const setStoreyVisibility = useModelStore(state => state.setStoreyVisibility)
  const setVisibleStoreys = useModelStore(state => state.setVisibleStoreys)
  const setTypeVisibility = useModelStore(state => state.setTypeVisibility)
  const toggleSingleStoreyVisibility = useModelStore(state => state.toggleSingleStoreyVisibility)
  const showComments = useModelStore(state => state.showComments)
  const setShowComments = useModelStore(state => state.setShowComments)

  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const setShowIndicators = useTapelineStore(state => state.setShowIndicators)
  const setIndicatorType = useTapelineStore(state => state.setIndicatorType)
  const isTapelineActive = useTapelineStore(state => state.isActive)

  const setOrthographicDrawMode = useControlStore(state => state.setOrthographicDrawMode)

  const elementTypes = useElementTypes()
  const activeElementType = activeElement ? elementTypes[activeElement] : null

  const [isEditing, setIsEditing] = useState(false)

  const canSelectWalls =
    (!isDrawMode || isWallRipMode || mode === 'draw-openings') && !isEditing && !isTapelineActive

  const wall = useMemo(
    () => find(visibleModel.walls, ['guid', activeElement]),
    [visibleModel, activeElement],
  )

  const beam = useMemo(
    () => find(visibleModel.beams, ['guid', activeElement]),
    [visibleModel, activeElement],
  )

  const purlin = useMemo(
    () => find(visibleModel.purlins, ['guid', activeElement]),
    [visibleModel, activeElement],
  )

  const availableNonRoofStoreys = useMemo(
    () => reject([...availableStoreys], availableStorey => availableStorey === roofStoreyKey),
    [availableStoreys],
  )

  // EFFECTS

  useTypeInteraction('none')

  // `invisible` is owned by SceneControlled (derived from the storey / orthographic draw mode) —
  // toggling it here made leaving this page reveal the 3D model while the camera stayed 2D.

  useEffect(() => {
    if (isOrthographic) {
      setShowIndicators(false)
      setIndicatorType('crosshair')
    }

    return () => {
      setShowIndicators(true)
      setIndicatorType('sphere')
    }
  }, [isOrthographic, setIndicatorType, setShowIndicators])

  useEffect(() => {
    const orthoGraphicDrawingMode = !!storey

    setOrthographicDrawMode(orthoGraphicDrawingMode)

    return () => setOrthographicDrawMode(false)
  }, [setOrthographicDrawMode, storey])

  // Combined single press and long press shortcuts for storey visibility
  useHotkeys_0to9WithNumpadAndLongPress(
    // Single press: toggle storey visibility
    storeyKey => {
      if (availableNonRoofStoreys.includes(storeyKey)) {
        toggleSingleStoreyVisibility(storeyKey)
      }
    },
    // Long press: show only selected storey
    storeyKey => {
      if (availableNonRoofStoreys.includes(storeyKey)) {
        // Set all available storeys to invisible
        availableNonRoofStoreys.forEach(storey => {
          setStoreyVisibility(storey, false)
        })
        setStoreyVisibility(roofStoreyKey, false)
        // Set the pressed storey to visible
        setStoreyVisibility(storeyKey, true)
      }
    },
    {},
    [visibleModel],
  )

  useHotkeys_ShortAndLongPress(
    ['r'],
    () => {
      setTypeVisibility('vertical_roof_slabs' as ElementTypes, !showVerticalRoofSlabs)
    },
    () => {
      setTypeVisibility('roof_slabs' as ElementTypes, !showRoofSlabs)
    },
    {},
    [showVerticalRoofSlabs, showRoofSlabs],
  )

  useHotkeys(
    ['shift+r'],
    () => toggleSingleStoreyVisibility(roofStoreyKey),
    { preventDefault: true },
    [visibleModel, showVerticalRoofSlabs, showRoofSlabs],
  )

  useHotkeys(
    ['p'],
    () => {
      setTypeVisibility('purlins' as ElementTypes, !showPurlins)
    },
    {},
    [showPurlins],
  )

  useHotkeys(
    ['c'],
    () => {
      setShowComments(!showComments)
    },
    {},
    [showComments],
  )

  useHotkeys_Ctrl_0to9(
    storeyKey => {
      if (availableNonRoofStoreys.includes(storeyKey)) {
        setStorey(storeyKey)
      }
    },
    {},
    [visibleModel],
  )

  // toggle storey image
  useHotkeys(
    ['f'],
    () => {
      storey && toggleStoreyImageVisibilityIn2D(storey)
    },
    { preventDefault: true },
    [storey, toggleStoreyImageVisibilityIn2D],
  )

  // hide all non roof storeys when SHIFT+r is pressed
  useHotkeys(
    ['CTRL+r'],
    () => {
      setVisibleStoreys([roofStoreyKey])
    },
    { preventDefault: true },
    [visibleModel],
  )

  useHotkeys_ShortAndLongPress(
    ['d'],
    () => {
      setTypeVisibility('vertical_slabs' as ElementTypes, !showVerticalSlabs)
    },
    () => {
      setTypeVisibility('slabs' as ElementTypes, !showSlabs)
    },
    {},
    [showVerticalSlabs, showSlabs],
  )

  useEffect(() => {
    if ((mode === 'draw-beams' || mode === 'draw-columns') && storey === roofStoreyKey) {
      enqueueSnackbar('Aktuell ist es nicht möglich Unterzüge oder Stützen im Dach zu zeichen.', {
        variant: 'warning',
        key: 'beam-column-in-roof',
      })
    }
    return () => {
      closeSnackbar('beam-column-in-roof')
    }
  }, [mode, storey])

  const onClickElement = useCallback(
    (guid: string) => {
      if (isSelectionMode) return

      if (canSelectWalls) {
        setActiveElement(guid)
        if (mode === 'draw-openings') {
          setStorey(null)
        }
      }
    },
    [setActiveElement, canSelectWalls, isSelectionMode, mode, setStorey],
  )

  useFloorplanClickListener(onClickElement)

  const hideActiveElement =
    activeElement &&
    (activeElementType === 'inner_walls' ||
      activeElementType === 'outer_walls' ||
      activeElementType === 'beams' ||
      activeElementType === 'purlins') &&
    !isWallRipMode &&
    !isCoordinateInputMode
      ? activeElement
      : null

  useHideFloorplanElements(hideActiveElement)

  const savedMouseDownPosition = useRef<Vector2>(new Vector2())
  const handleMouseDown = useCallback((event: HTMLElementEventMap['mousedown']) => {
    savedMouseDownPosition.current = new Vector2(event.clientX, event.clientY)
  }, [])
  useCanvasEvent('mousedown', handleMouseDown)

  const elementTypeToSelectable = useMemo<Record<ElementTypes, boolean>>(() => {
    return {
      outer_walls: true,
      inner_walls: true,
      slabs: isDrawingPurlinsMode,
      roof_slabs: true,
      vertical_slabs: true,
      vertical_roof_slabs: true,
      beams: true,
      purlins: true,
      columns: true,
      rips: true,
      lintels: true,
      slab_beams: true,
      roof_slab_beams: true,
      standard_rips: true,
      foundation: true,
    }
  }, [isDrawingPurlinsMode])

  useFloorplanElementTypeToSelectable(elementTypeToSelectable)

  return (
    <>
      <group>
        {mode === 'draw-beams' && <BeamDrawer storey={storey as string} enabled={true} />}
        {mode === 'draw-walls' && storey && <WallDrawer activeStorey={storey} />}
        {(activeElementType === 'inner_walls' || activeElementType === 'outer_walls') &&
          mode !== 'wall-rip' &&
          !isCoordinateInputMode &&
          activeElement &&
          wall && (
            <WallEdit
              wallGuid={activeElement}
              onEditStart={() => setIsEditing(true)}
              onEditStop={() => setIsEditing(false)}
              reset={() => setActiveElement(null)}
            />
          )}

        {activeElementType === 'beams' && activeElement && beam && (
          <BeamEdit
            beamGuid={activeElement}
            onEditStart={() => setIsEditing(true)}
            onEditStop={() => setIsEditing(false)}
            reset={() => setActiveElement(null)}
          />
        )}

        {activeElementType === 'purlins' && activeElement && purlin && (
          <PurlinEdit
            purlinGuid={activeElement}
            onEditStart={() => setIsEditing(true)}
            onEditStop={() => setIsEditing(false)}
            reset={() => setActiveElement(null)}
          />
        )}

        {mode === 'draw-vertical-slabs' && <VerticalSlabDrawer2D storey={storey as string} />}
        {mode === 'draw-columns' && <ColumnDrawer storey={storey as string} />}
        {mode === 'draw-purlins' && <PurlinDrawer />}
        {mode === 'draw-vertical-roofs' && <VerticalRoofDrawer2D storey={storey as string} />}
      </group>
    </>
  )
}

export default FloorplanDrawer
export { getSnapLinesFromPolygon } from './utils'
